import React from 'react';
import { MenuItem, Select } from '@material-ui/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IQuizFormInput } from '../../../quiz.types';
import { StyledFieldError } from '../../../styles';
import { getChoicesHint } from './helpers';
import {
  selectDisplayStyle,
  StyledChoiceChip,
  StyledDropdownChoicesHint,
  StyledDropdownFormControl,
  StyledSelectedChipsWrapper,
  StyledSelectPlaceholder,
} from './styles';

// ---------------------------------------------------------------------------
// Dropdown inner component (extracted to respect Rules of Hooks)
// ---------------------------------------------------------------------------
export type DropdownInnerProps = {
  input: IQuizFormInput;
  selected: string[];
  isSingleChoice: boolean;
  minAnswer: number;
  maxAnswer: number;
  error: string | undefined;
  onChange: (val: string[]) => void;
};

export function DropdownInner({
  input,
  selected,
  isSingleChoice,
  minAnswer: _minAnswer,
  maxAnswer,
  error,
  onChange,
}: DropdownInnerProps): JSX.Element {
  const { t } = useTranslation();
  const choices = input.formInputChoices;
  const [open, setOpen] = useState(false);

  const handleChange = (iri: string) => {
    let next: string[];
    if (isSingleChoice) {
      next = [iri];
    } else if (selected.includes(iri)) {
      next = selected.filter((v) => v !== iri);
    } else if (input.maxAnswer === null || selected.length < maxAnswer) {
      next = [...selected, iri];
    } else {
      next = selected;
    }
    onChange(next);
    setOpen(false);
  };

  return (
    <StyledDropdownFormControl variant="outlined" error={!!error}>
      <Select
        multiple={!isSingleChoice}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={isSingleChoice ? selected[0] ?? '' : selected}
        onChange={() => {
          // handled manually via MenuItem onClick
        }}
        displayEmpty
        SelectDisplayProps={{ style: selectDisplayStyle }}
        renderValue={
          isSingleChoice
            ? (sel) => {
                const iri = sel as string;
                if (!iri)
                  return (
                    <StyledSelectPlaceholder>
                      {t('challenges.declaration.questions.select')}
                    </StyledSelectPlaceholder>
                  );
                return choices.find((c) => c['@id'] === iri)?.title ?? iri;
              }
            : () => (
                <StyledSelectPlaceholder>
                  {t('challenges.declaration.questions.select')}
                </StyledSelectPlaceholder>
              )
        }
      >
        {isSingleChoice && (
          <MenuItem value="" disabled>
            <em>{t('challenges.declaration.questions.select')}</em>
          </MenuItem>
        )}
        {choices.map((c) => (
          <MenuItem
            key={c['@id']}
            value={c['@id']}
            selected={selected.includes(c['@id'])}
            onClick={() => handleChange(c['@id'])}
          >
            {c.title}
          </MenuItem>
        ))}
      </Select>

      {!isSingleChoice && selected.length > 0 && (
        <StyledSelectedChipsWrapper>
          {selected.map((iri) => {
            const choice = choices.find((c) => c['@id'] === iri);
            return (
              <StyledChoiceChip
                key={iri}
                label={choice?.title ?? iri}
                size="medium"
                onDelete={() => onChange(selected.filter((v) => v !== iri))}
              />
            );
          })}
        </StyledSelectedChipsWrapper>
      )}

      {!isSingleChoice && (
        <StyledDropdownChoicesHint variant="caption" color="textSecondary">
          {getChoicesHint(t, input.minAnswer ?? null, input.maxAnswer ?? null)}
        </StyledDropdownChoicesHint>
      )}
      {error && <StyledFieldError>{error}</StyledFieldError>}
    </StyledDropdownFormControl>
  );
}
